import React, { useEffect, useState } from 'react';
import './loading-screen.css';

// 五帧共享画布、像素比例和书脊锚点；展开后的投影宽度自然增加，不能逐帧适配外接框。
const BOOK_FRAMES = [
  ['loading-book-30.preview.webp', 'loading-book-30.webp', 'loading-book-30.png'],
  ['loading-book-60.preview.webp', 'loading-book-60.webp', 'loading-book-60.png'],
  ['loading-book-90.preview.webp', 'loading-book-90.webp', 'loading-book-90.png'],
  ['loading-book-120.preview.webp', 'loading-book-120.webp', 'loading-book-120.png'],
  ['loading-book-open.preview.webp', 'loading-book-open.webp', 'loading-book-open.png'],
] as const;

/**
 * 全局加载动画：播放五张固定画布的实体开合帧，避免 CSS 变换造成缩放和形变。
 * 播放中的帧只使用一个稳定资源；高清资源提前预加载，不在当前帧中途替换，避免最后出现二次抖动。
 * 导航切换若在门槛内完成，则完全不显示。
 */
export const LoadingScreen: React.FC<{ label?: string; immediate?: boolean }> = ({ label = '加载中', immediate = false }) => (
  <DelayedLoadingScreen label={label} immediate={immediate} />
);

const LOADING_DELAY_MS = 180;
const BOOK_FRAME_INTERVAL_MS = 420;

const DelayedLoadingScreen: React.FC<{ label: string; immediate: boolean }> = ({ label, immediate }) => {
  const [visible, setVisible] = useState(immediate);
  const [bookFrameIndex, setBookFrameIndex] = useState(0);

  useEffect(() => {
    if (immediate) return undefined;
    const timer = window.setTimeout(() => setVisible(true), LOADING_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [immediate]);

  useEffect(() => {
    if (!visible) return undefined;
    const timer = window.setInterval(() => {
      setBookFrameIndex((index) => Math.min(index + 1, BOOK_FRAMES.length - 1));
    }, BOOK_FRAME_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const preloadedImages = BOOK_FRAMES.flatMap(([previewSrc, highResolutionSrc]) => {
      const preview = new Image();
      const highResolution = new Image();
      preview.src = `/assets/${previewSrc}`;
      highResolution.src = `/assets/${highResolutionSrc}`;
      return [preview, highResolution];
    });
    return () => preloadedImages.forEach((image) => { image.onload = null; });
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fp-loading-screen" role="status" aria-label={label} data-testid="loading-screen">
      <div className="fp-loading-book-stage" aria-hidden="true">
        {(() => {
          const [, highResolutionSrc, fallbackSrc] = BOOK_FRAMES[bookFrameIndex];
          return (
            <img
              className="fp-loading-book-frame fp-loading-book-frame--active"
              data-book-frame={bookFrameIndex}
              src={`/assets/${highResolutionSrc}`}
              onError={(event) => { event.currentTarget.src = `/assets/${fallbackSrc}`; }}
              alt=""
            />
          );
        })()}
      </div>
      <div className="fp-loading-paws" aria-hidden="true">
        {Array.from({ length: 10 }, (_, index) => (
          <picture key={index}>
            <source srcSet="/assets/loading-paw.webp" type="image/webp" />
            <img
              className={index % 2 === 1 ? 'fp-loading-paw fp-loading-paw--sage' : 'fp-loading-paw'}
              src="/assets/loading-paw.png"
              alt=""
            />
          </picture>
        ))}
      </div>
    </div>
  );
};

export default LoadingScreen;
